import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import NoComments from '@/components/Illustrations/NoComments';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import ProfilePicture from '@/components/ui/profile-picture';
import LexicalEditor from '@/components/ui/lexical-editor';
import ProfileLink from '@/components/Post/ProfileLink';
import { cn, formatRelativeTime } from '@/lib/utils';
import CommentComposer, { plainTextState } from './CommentComposer';
import CommentHeartButton from './CommentHeartButton';
import type {
  ICommentThread,
  ICommentThreadItem,
} from '@/redux/commet/comment.d';

type ReplyHandler = (
  parentCommentId: string,
  serializedState: string
) => Promise<void>;

type RenderDelete = (comment: ICommentThreadItem) => ReactNode;

function AuthorAvatar({
  user,
  isReply,
}: {
  user: ICommentThreadItem['user'];
  isReply: boolean;
}) {
  const username = user?.username;

  return (
    <ProfileLink username={username} className="shrink-0">
      <ProfilePicture
        src={user?.profilePicture}
        alt={username}
        className={isReply ? 'w-6 h-6' : 'w-8 h-8'}
        username={username}
      />
    </ProfileLink>
  );
}

function RowHeader({
  comment,
  renderDelete,
}: {
  comment: ICommentThreadItem;
  renderDelete: RenderDelete;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <ProfileLink
        username={comment.user?.username}
        className="font-medium text-sm"
      >
        @{comment.user?.username}
      </ProfileLink>
      <span className="text-xs text-muted-foreground">
        {formatRelativeTime(comment.commentedAt)}
      </span>
      <div className="ml-auto text-muted-foreground">
        {renderDelete(comment)}
      </div>
    </div>
  );
}

function RowBody({ comment }: { comment: ICommentThreadItem }) {
  // Pasted images arrive at their natural size — cap them so one screenshot
  // can't dwarf every other comment in the list.
  return (
    <div className="text-sm mt-0.5 [&_img]:max-w-full [&_img]:max-h-64 [&_img]:w-auto [&_img]:rounded-md [&_img]:my-2">
      <LexicalEditor
        readOnly
        initialState={comment.comment}
        contentClassName="px-0 py-0 min-h-0"
      />
    </div>
  );
}

// Named for its target: a page of threads otherwise has many "Reply" buttons.
function RowActions({
  comment,
  onReply,
}: {
  comment: ICommentThreadItem;
  onReply: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <CommentHeartButton
        commentId={comment._id}
        liked={comment.hasLikedByUser}
        count={comment.likeCount}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={`Reply to @${comment.user?.username ?? 'comment'}`}
        className="h-7 px-2 text-xs text-muted-foreground"
        onClick={onReply}
      >
        Reply
      </Button>
    </div>
  );
}

function CommentRow({
  comment,
  renderDelete,
  onReply,
  isReply = false,
  children,
}: {
  comment: ICommentThreadItem;
  renderDelete: RenderDelete;
  onReply: () => void;
  isReply?: boolean;
  children?: ReactNode;
}) {
  // Kept so the thread under it still reads; the body, author and heart are gone.
  if (comment.isDeleted) {
    return (
      <li className="py-3 first:pt-0">
        <p className="text-sm italic text-muted-foreground">Comment deleted</p>
        {children}
      </li>
    );
  }

  return (
    <li
      id={`comment-${comment._id}`}
      className={cn(
        'flex scroll-mt-24 gap-3 py-3 first:pt-0',
        isReply && 'gap-2'
      )}
    >
      <AuthorAvatar user={comment.user} isReply={isReply} />
      <div className="flex-1 min-w-0">
        <RowHeader comment={comment} renderDelete={renderDelete} />
        <RowBody comment={comment} />
        <RowActions comment={comment} onReply={onReply} />
        {children}
      </div>
    </li>
  );
}

function ReplyList({
  replies,
  renderDelete,
  onReplyTo,
}: {
  replies: ICommentThreadItem[];
  renderDelete: RenderDelete;
  onReplyTo: (reply: ICommentThreadItem) => void;
}) {
  return (
    <ul className="mt-1 flex flex-col border-l border-border pl-3">
      {replies.map((reply) => (
        <CommentRow
          key={reply._id}
          comment={reply}
          renderDelete={renderDelete}
          onReply={() => onReplyTo(reply)}
          isReply
        />
      ))}
    </ul>
  );
}

function repliesLabel(count: number, expanded: boolean): string {
  if (expanded) return 'Hide replies';
  return `${count} ${count === 1 ? 'reply' : 'replies'}`;
}

/** "3 replies" / "Hide replies", the whole thread behind one toggle. */
function RepliesToggle({
  count,
  expanded,
  onToggle,
}: {
  count: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (count === 0) return null;

  const Icon = expanded ? ChevronUp : ChevronDown;

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="-ml-2 mt-1 h-7 gap-1.5 px-2 text-xs font-medium text-primary"
      onClick={onToggle}
    >
      <Icon className="h-4 w-4" />
      {repliesLabel(count, expanded)}
    </Button>
  );
}

/**
 * Replying to a reply still attaches to the top-level comment — the server
 * re-points it either way — so the @mention is what carries who was answered.
 */
function replyPrefill(
  target: ICommentThreadItem,
  threadId: string
): string | undefined {
  const username = target.user?.username;
  if (!username || target._id === threadId) return undefined;
  return plainTextState(`@${username} `);
}

function Thread({
  thread,
  renderDelete,
  onReply,
}: {
  thread: ICommentThread;
  renderDelete: RenderDelete;
  onReply: ReplyHandler;
}) {
  const [replyingTo, setReplyingTo] = useState<ICommentThreadItem | null>(null);
  const [expanded, setExpanded] = useState(false);

  const replies = thread.replies ?? [];

  return (
    <CommentRow
      comment={thread}
      renderDelete={renderDelete}
      onReply={() => {
        setReplyingTo(thread);
        setExpanded(true);
      }}
    >
      <RepliesToggle
        count={replies.length}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
      />
      {expanded && (
        <ReplyList
          replies={replies}
          renderDelete={renderDelete}
          onReplyTo={setReplyingTo}
        />
      )}
      {replyingTo && (
        <div className="mt-2">
          <CommentComposer
            variant="compact"
            submitLabel="Reply"
            placeholder="Write a reply"
            initialState={replyPrefill(replyingTo, thread._id)}
            onCancel={() => setReplyingTo(null)}
            onSubmit={async (body) => {
              await onReply(thread._id, body);
              setReplyingTo(null);
              setExpanded(true);
            }}
          />
        </div>
      )}
    </CommentRow>
  );
}

function LoadingRows() {
  return (
    <div className="flex flex-col gap-5">
      {Array.from({ length: 3 }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="flex gap-3" key={index}>
          <Skeleton className="rounded-full w-8 h-8 shrink-0" />
          <div className="flex flex-col gap-2 flex-1">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center text-center gap-3 py-2">
      <div className="w-40 md:w-52 [&>svg]:w-full [&>svg]:h-auto">
        <NoComments />
      </div>
      <div>
        <h1 className="font-primary font-bold text-xl">No comments yet</h1>
        <p className="font-secondary text-sm text-muted-foreground">
          your thoughts could lead the way
        </p>
      </div>
    </div>
  );
}

/** Scroll to a #comment-<id> deep link once the threads have rendered. */
function useScrollToLinkedComment(comments: ICommentThread[]) {
  useEffect(() => {
    const { hash } = window.location;
    if (!hash.startsWith('#comment-') || comments.length === 0) return;
    document
      .getElementById(hash.slice(1))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [comments]);
}

/**
 * The comment list for a post or a project group. The caller supplies the
 * fetched threads, the delete control and the reply mutation; everything the
 * two surfaces render is the same and lives here.
 */
export default function CommentThreadList({
  comments,
  isLoading,
  renderDelete,
  onReply,
}: {
  comments: ICommentThread[];
  isLoading: boolean;
  renderDelete: RenderDelete;
  onReply: ReplyHandler;
}) {
  useScrollToLinkedComment(comments);
  if (isLoading) return <LoadingRows />;
  if (comments.length === 0) return <EmptyState />;

  // ponytail: plain list. The old CommentsTimeline was a fixed 600px scroll
  // container with scroll-position-driven active-item logic — it did not
  // survive being put in a half-width column.
  return (
    <ul className="flex flex-col divide-y divide-border">
      {comments.map((thread) => (
        <Thread
          key={thread._id}
          thread={thread}
          renderDelete={renderDelete}
          onReply={onReply}
        />
      ))}
    </ul>
  );
}
